import { Link } from "react-router-dom";
import { ArrowRight, Home, ShieldAlert } from "lucide-react";

import EmptyState from "@/components/common/EmptyState";
import PageHeader from "@/components/layout/PageHeader";
import { useGetMyDeals } from "@/hooks/useDeals";
import { DEAL_SIDE_LABELS } from "@/constant/deals.constant";
import { absoluteUrl, formatUtcDate } from "@/utils/common.utils";
import type { MyDealSummary } from "@/types/deals.types";

/**
 * Every room this person has been invited into.
 *
 * **The gap this closes.** One email address is one account, and a participant
 * role is stored per `(deal, user)` — so the same client genuinely can be
 * selling one house and buying another, and the same attorney can be closing
 * four. Every external surface, though, read `deal_participations[0]` and
 * rendered that single deal: the second room existed, was fully authorised, and
 * had no link pointing at it anywhere in the product.
 *
 * Reads from `GET /me/deals`, which is a privacy-safe projection rather than the
 * deal document — no prices, no notes, nothing the calm view withholds.
 */
export default function ExternalDeals() {
  const { data, isLoading, isError } = useGetMyDeals();
  const deals = data?.deals ?? [];

  if (isLoading) return <ExternalDealsSkeleton />;

  if (isError) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="We couldn’t load your deals"
        description="Refresh the page, or ask your agent to resend your invitation."
        className="mx-auto mt-20 max-w-md"
      />
    );
  }

  if (!deals.length) {
    return (
      <EmptyState
        icon={Home}
        title="No deals shared with you yet"
        description="When an agent invites you into a transaction, it will appear here."
        className="mx-auto mt-20 max-w-md"
      />
    );
  }

  return (
    <section className="font-dashboard" data-testid="external-deals">
      <PageHeader
        className="mb-8"
        title="Your deals"
        description={`${deals.length} ${deals.length === 1 ? "transaction" : "transactions"} shared with you.`}
      />

      <ul className="grid gap-4 sm:grid-cols-2">
        {deals.map((deal: MyDealSummary) => (
          <li key={deal.deal_id}>
            <DealCard deal={deal} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function DealCard({ deal }: { deal: MyDealSummary }) {
  const sideLabel = DEAL_SIDE_LABELS[deal.my_side as keyof typeof DEAL_SIDE_LABELS];
  const closing = formatUtcDate(deal.close_date, { year: "numeric" });

  return (
    <Link
      to={deal.route}
      className="group flex h-full flex-col gap-5 overflow-hidden rounded-xl border border-dash-border bg-white p-6 transition-colors hover:border-dash-greenMid"
      data-testid={`external-deal-card-${deal.deal_id}`}
    >
      {deal.hero_image_url && (
        <img
          src={absoluteUrl(deal.hero_image_url)}
          alt={`Photo of ${deal.address}`}
          loading="lazy"
          className="-mx-6 -mt-6 h-32 w-[calc(100%+3rem)] max-w-none object-cover"
        />
      )}
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-dash-muted">
          {deal.role_label}
          {sideLabel && <><span className="mx-2 text-dash-faint">.</span>{sideLabel}</>}
        </div>
        <h2 className="mt-2 truncate text-xl font-semibold text-dash-ink" title={deal.address}>
          {deal.address}
        </h2>
        <div className="mt-1 text-[13px] text-dash-muted">{deal.phase_label}</div>
      </div>

      {/* Only rendered when there is something to count. A 0% bar on a room whose
          checklist has not been seeded yet reads as "nothing is happening",
          which is the opposite of true. */}
      {deal.tasks_total > 0 && (
        <div>
          <div className="flex items-baseline justify-between text-[12px] text-dash-muted">
            <span>{deal.tasks_complete} of {deal.tasks_total} done</span>
            <span className="font-semibold text-dash-ink">{deal.progress_pct}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-dash-chip">
            <div
              className="h-full rounded-full bg-dash-greenMid transition-[width] duration-500"
              style={{ width: `${deal.progress_pct}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-auto flex items-center justify-between gap-4 border-t border-dash-border pt-4 text-[13px]">
        <span className="min-w-0 truncate text-dash-muted">
          {closing ? `Closing ${closing}` : deal.agent.name || " "}
        </span>
        <span className="flex shrink-0 items-center gap-1 font-medium text-dash-greenDeep">
          Open
          <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function ExternalDealsSkeleton() {
  return (
    <div className="animate-pulse font-dashboard" data-testid="external-deals-skeleton">
      <div className="h-9 w-48 rounded-lg bg-dash-chip" />
      <div className="mb-8 mt-2 h-4 w-64 rounded bg-dash-chip" />
      <div className="grid gap-4 sm:grid-cols-2">
        {[0, 1].map((index) => (
          <div key={index} className="h-52 rounded-xl border border-dash-border bg-white" />
        ))}
      </div>
    </div>
  );
}
